import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, HeartHandshake, Clock } from "lucide-react";
import { MaskedLines } from "@/components/site/reveal";
import { scrollToSection } from "@/components/site/Nav";

const HERO_IMG = "/caregiver-1.jpg";

const TRUST = [
  { icon: ShieldCheck, label: "Licensed & insured" },
  { icon: HeartHandshake, label: "Background-checked caregivers" },
  { icon: Clock, label: "Available 7 days a week" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative bg-navy-dark overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-44 pb-24 md:pt-56 md:pb-32 grid lg:grid-cols-12 gap-16 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 lg:pr-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="flex items-center gap-4 mb-10"
          >
            <span className="h-px w-12 bg-silver" />
            <span data-testid="hero-overline" className="text-xs font-bold uppercase tracking-[0.25em] text-silver">
              In-home senior care
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display font-light tracking-tighter leading-[0.95] text-white text-5xl sm:text-6xl lg:text-8xl"
          >
            <MaskedLines
              lines={[
                "Care that",
                "truly feels",
                <span key="l3">like <em className="italic text-silver">family.</em></span>,
              ]}
            />
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-md text-base md:text-lg leading-relaxed text-white/70"
          >
            Karen's Caring Hands brings warm, dependable in-home care to the people you love —
            so they can stay safe, comfortable, and independent in the home they know.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToSection("contact")}
              className="group inline-flex items-center gap-3 bg-white text-navy-dark text-xs font-bold uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-silver-light transition-colors duration-300"
            >
              Request a care visit
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToSection("services")}
              className="text-xs font-bold uppercase tracking-[0.18em] text-white border-b border-white/40 pb-1 hover:border-white transition-colors duration-300"
            >
              Explore services
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-trust-row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-16 flex flex-wrap gap-x-8 gap-y-3"
          >
            {TRUST.map((t) => (
              <span key={t.label} className="inline-flex items-center gap-2 text-xs text-white/60">
                <t.icon className="w-4 h-4 text-silver" />
                {t.label}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-w-sm mx-auto lg:ml-auto"
          >
            <div aria-hidden className="absolute -top-6 -right-6 w-full h-full bg-silver rounded-t-[999px]" />
            <div className="relative overflow-hidden rounded-t-[999px] border border-white/30 aspect-[3/4] bg-silver-light">
              <motion.img
                data-testid="hero-image"
                src={HERO_IMG}
                alt="A Karen's Caring Hands caregiver in her navy blue uniform"
                style={{ y: imgY }}
                className="w-full h-[118%] object-cover scale-105"
              />
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white border border-silver px-6 py-5 shadow-[0_20px_50px_-20px_rgba(16,43,85,0.25)]">
              <p className="font-display text-4xl text-navy-dark leading-none">10+</p>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-navy-dark/60 mt-2">
                Years of devoted care
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
